/**
 * Runner identity base traits — exact mirror of contracts/src/identity/IdentityTraits.sol. Derived only from the
 * deployment-committed trait seed and the token id; never from owner, equipment or time.
 */
import { encodeAbiParameters, hexToBytes, keccak256, type Hex } from 'viem';

export const TRAIT_SIZES = [4, 5, 4, 4, 4, 4] as const;
/**
 * Trait families follow the brand collectible composition (brand/source/collectibles): portrait frame, the canonical
 * head's eye shape, headband, accent colour (frame and chest stripe), background field and singlet. The head itself —
 * three swept tufts, cream eyes, no mouth — is the same for every Runner (brand/docs/CHARACTER_BIBLE.md).
 */
export const TRAIT_NAMES = {
  frame: ['Ring', 'Rays', 'Track', 'Plain'],
  eyes: ['Oval', 'Focused', 'Wide', 'Sly', 'Arched'],
  headband: ['None', 'Orange', 'Cream', 'Gold'],
  accent: ['Orange', 'Blue', 'Green', 'Gold'],
  field: ['Cream', 'Paper', 'Sand', 'Mist'],
  singlet: ['Cream', 'Sand', 'Cream, piped', 'Sand, piped'],
} as const;

/** Display titles, in on-chain attribute order. */
export const TRAIT_TITLES: [keyof typeof TRAIT_NAMES, string][] = [
  ['frame', 'Frame'],
  ['eyes', 'Eyes'],
  ['headband', 'Headband'],
  ['accent', 'Accent'],
  ['field', 'Field'],
  ['singlet', 'Singlet'],
];

export type BaseTraits = [number, number, number, number, number, number];

export function baseTraits(seed: Hex, tokenId: bigint): BaseTraits {
  const h = hexToBytes(keccak256(encodeAbiParameters([{ type: 'bytes32' }, { type: 'uint256' }], [seed, tokenId])));
  return TRAIT_SIZES.map((size, i) => h[i] % size) as BaseTraits;
}

export function traitLabels(t: BaseTraits): Record<keyof typeof TRAIT_NAMES, string> {
  return {
    frame: TRAIT_NAMES.frame[t[0]],
    eyes: TRAIT_NAMES.eyes[t[1]],
    headband: TRAIT_NAMES.headband[t[2]],
    accent: TRAIT_NAMES.accent[t[3]],
    field: TRAIT_NAMES.field[t[4]],
    singlet: TRAIT_NAMES.singlet[t[5]],
  };
}
